import React, { useState, useEffect, useCallback } from 'react'
import {
  FlaskConical, Play, Loader2, CheckCircle2, XCircle, Terminal,
  AlertTriangle, RefreshCw, ChevronRight,
} from 'lucide-react'
import { reverseBacktest, exitReplay } from '../lib/api'
import OptimizerSummaryPanel from '../components/OptimizerSummaryPanel'
import ReconstructionPanel from '../components/ReconstructionPanel'
import StrategyCardModal from '../components/StrategyCardModal'
import { fmtPct, timeAgo } from '../lib/utils'

// T-365 [LIVE 2026-11-04 shot]: card descriptions ran about 180 characters a line at 1366; capped at 80ch for reading.
// ── Past test types -- description only, per owner directive (these are
// CLI-only scripts against real trade_exits/active_signals data; none of
// them are wired to an on-demand backend endpoint, so this page doesn't
// pretend otherwise -- see docs/BACKTEST_GUIDE.md for the full reference). ──
const PAST_TESTS = [
  {
    id: 'current_system',
    name: 'Current System Backtest',
    tagline: 'Gate analysis against real trade history',
    description: 'Works entirely from real production outcomes (trade_exits joined to '
      + 'active_signals) rather than re-simulating price bars -- if it happened in the '
      + 'database, it happened in reality. Covers baseline win rate / profit factor, '
      + 'confidence / volume / RSI gate sensitivity, a confidence × volume optimizer '
      + 'grid, sector and symbol ranking, and an auto-generated data-driven '
      + 'recommendations summary across roughly 15 sections.',
    script: 'scripts/archive/backtests/backtest_current.py',
    usage: 'python scripts/archive/backtests/backtest_current.py --since 2026-05-05',
    docs: 'docs/BACKTEST_GUIDE.md',
  },
  {
    id: 'signal_quality',
    name: 'Signal Quality Deep-Dive',
    tagline: 'Targeted comparisons between signal types',
    description: 'Focused comparisons between signal types (e.g. SUPERTREND_BULLISH vs '
      + 'MACD_BULLISH) -- this is how MACD was found to structurally fire in low-volume '
      + 'conditions and got disabled. Used for one specific question at a time rather '
      + 'than a full sweep.',
    script: 'scripts/archive/backtests/backtest_signal_quality.py',
    usage: 'python scripts/archive/backtests/backtest_signal_quality.py',
    docs: 'docs/BACKTEST_GUIDE.md',
  },
  {
    id: 'gate_optimizer',
    name: 'Gate Optimizer Grid Search',
    tagline: 'Broader parameter-space sweep',
    description: 'Grid search across a wider parameter space than the current-system '
      + 'backtest\'s built-in confidence × volume optimizer -- used when a question '
      + 'spans more than two gate parameters at once.',
    script: 'scripts/archive/backtests/backtest_optimization.py',
    usage: 'python scripts/archive/backtests/backtest_optimization.py',
    docs: 'docs/BACKTEST_GUIDE.md',
  },
  {
    // T-274 (2026-10-19): offline Tier 2 Control-vs-Variant simulation harness; CLI only, no orders, no production writes
    // id uses letters and underscores only: tests/test_testing_card_info.py matches ids with [a-z_]+ on a line of its own
    id: 'simulation_harness',
    name: 'Tier 2 Simulation Harness',
    tagline: 'Control vs Variant over stored 1-minute bars · Exploratory · local CLI',
    description: 'Replays the live Tier 2 rules (current config applied retroactively) over '
      + 'stored bars for a Control arm and a Variant arm with the same parameters, calibrated '
      + 'fills and costs, then reports P&L, true mark-to-market drawdown and a paired, '
      + 'day-block bootstrap comparison at 0.5x, 1x and 2x friction. Label Exploratory: '
      + 'fidelity on the held-out day 2026-10-16 is exact-code 98 %, but recall 90 %, precision '
      + '80 % and exit match 89 % are below the 99 / 99 / 95 targets. First screen: a 0.08 % '
      + 'spread cap is a hypothesis (+$69 at 1x with an interval from -$120 to +$238, which spans zero), '
      + 'not a go-ahead. Relative evidence only.',
    script: 'scripts/ops/sim_harness/runner.py',
    usage: 'python scripts/ops/sim_harness/runner.py --run-name NAME --first-day 2026-10-09 --last-day 2026-10-16 --snapshots snapshots_cur.jsonl  (local files under exports/sim_runs, no database)',
    docs: 'docs/reports/TIER2_SIM_HARNESS_RUNBOOK_2026_10_18.md',
  },
  {
    // T-277 (2026-10-19): the canonical offline evaluation (causal parameters + replay + live comparison); CLI only, advisory output, no production writes beyond the T-211 tables
    // id uses letters and underscores only: tests/test_testing_card_info.py matches ids with [a-z_]+ on a line of its own
    id: 'causal_backtest',
    name: 'Causal Backtest (full run)',
    tagline: 'Point-in-time parameters + Control vs Variant + live comparison · Exploratory · resumable',
    description: 'The single offline evaluation path. One command after the close rebuilds the '
      + 'ticker parameters as they were known at each moment (no look-ahead), replays Control and '
      + 'Variant minute by minute at 1x and 2x friction, and compares the replay with the live '
      + 'Tier 2 trades that happened. The summary gives net expectancy with intervals, drawdown '
      + 'and fold consistency, and an advisory recommendation that never changes a live '
      + 'parameter. Runs for hours without blocking a deploy: it resumes within about five '
      + 'minutes of a kill and pauses database-heavy stages during market hours. Refuses or '
      + 'labels itself non-decision-grade when data health is red. Replaces the fill-at-close '
      + 'engine of the Reverse Indicator Search, which is deprecated as evidence.',
    script: 'scripts/ops/run_full_causal_backtest.py',
    // Linux cutover 2026-11-01, Doc 50: wording no longer assumes Railway (T-323)
    usage: '<python> scripts/ops/run_full_causal_backtest.py --window full --variant NAME=\'{"tier2_entry_max_spread_pct": 0.08}\' --friction 1 2  (add --dry-run first; --tick resumes)',
    docs: 'docs/reports/CAUSAL_BACKTEST_RUNBOOK_2026_10_19.md',
  },
  {
    id: 'today_snapshot',
    name: "Today's Activity Snapshot",
    tagline: 'Daily pulse check, not a historical backtest',
    description: "Today's exits, currently open positions, and recent gate decisions -- "
      + 'a quick "what happened so far today" read rather than a historical P&L study.',
    script: 'scripts/archive/ops_oneoffs/analyze_today.py',
    usage: 'python scripts/archive/ops_oneoffs/analyze_today.py',
    docs: 'docs/BACKTEST_GUIDE.md',
  },
]

const REVERSE_BACKTEST_ID = 'reverse_indicator_search'
const OPTIMIZER_ID = 'walk_forward_optimizer'
const EXIT_REPLAY_ID = 'counterfactual_exit_replay'
const RECONSTRUCTION_ID = 'param_reconstruction'
const REVERSE_BACKTEST_DESCRIPTION =
  'Tests each technical indicator alone, at its most common textbook setting -- RSI-14 '
  + '30/70, Stochastic %K/%D-14 20/80, MFI-14 20/80, MACD-12/26/9 histogram zero-cross, '
  + 'SuperTrend-10/3 flip, EMA-20 / EMA-50 / SMA-20 / VWAP 0.2% stretch, and Bollinger '
  + '20/2 lower-band-to-middle-band -- against every ticker in the database, over the '
  + 'full 90-day, 1-minute bar history in tactical_bars_90day. Every symbol × '
  + 'indicator result is logged, nothing is discarded. The best individual performers '
  + '(minimum trade count required) then get tested in pairwise and 3-way combinations, '
  + 'hunting for a rare combination with a real, consistent edge. Same realistic '
  + 'spread-based fill pricing and friction convention as the rest of this project\'s '
  + 'backtests, plus a session-close flatten so a 1-minute-interval test can\'t carry a '
  + 'position across an overnight gap. DEPRECATED as evidence (T-277): it fills at the '
  + 'signal bar\'s close with no delay option; use the Causal Backtest card.'

const POLL_MS = 4000

function StateBadge({ state }) {
  if (state === 'running') {
    return (
      <span className="inline-flex items-center gap-1.5 text-amber-400" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.68rem' }}>
        <Loader2 className="h-3 w-3 animate-spin" /> RUNNING
      </span>
    )
  }
  if (state === 'completed') {
    return (
      <span className="inline-flex items-center gap-1.5 text-emerald-400" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.68rem' }}>
        <CheckCircle2 className="h-3 w-3" /> COMPLETED
      </span>
    )
  }
  if (state === 'failed') {
    return (
      <span className="inline-flex items-center gap-1.5 text-rose-400" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.68rem' }}>
        <XCircle className="h-3 w-3" /> FAILED
      </span>
    )
  }
  return null
}

function ResultsTable({ title, rows }) {
  const shown = (rows || []).filter(r => r.n_symbols_significant > 0)
  if (shown.length === 0) return null
  return (
    <div className="rounded-lg border border-slate-800 overflow-hidden" style={{ background: '#0d1321' }}>
      <div className="px-4 py-3 border-b border-slate-800">
        <p className="text-slate-400 text-xs tracking-widest font-semibold">{title}</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-slate-600 border-b border-slate-800/60">
              <th className="px-4 py-2 text-left">Indicator</th>
              <th className="px-4 py-2 text-right">Symbols</th>
              <th className="px-4 py-2 text-right">Trades</th>
              <th className="px-4 py-2 text-right">Mean Avg%/Trade</th>
              <th className="px-4 py-2 text-right">Win Rate</th>
              <th className="px-4 py-2 text-right">% Profitable</th>
              <th className="px-4 py-2 text-right">% Stable</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r, i) => (
              <tr key={r.indicator} className={`border-b border-slate-800/30 hover:bg-slate-800/20 ${i === 0 ? 'bg-cyan-500/5' : ''}`}>
                <td className="px-4 py-2.5 text-slate-300 font-mono">{r.indicator}</td>
                <td className="px-4 py-2.5 text-right text-slate-400 font-mono">{r.n_symbols_significant}/{r.n_symbols}</td>
                <td className="px-4 py-2.5 text-right text-slate-400 font-mono">{r.total_trades}</td>
                <td className={`px-4 py-2.5 text-right font-mono font-semibold ${r.mean_avg_pct_per_trade >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                  {fmtPct(r.mean_avg_pct_per_trade, true)}
                </td>
                <td className="px-4 py-2.5 text-right text-slate-400 font-mono">{r.mean_win_rate?.toFixed(1)}%</td>
                <td className="px-4 py-2.5 text-right text-slate-400 font-mono">{r.pct_symbols_profitable?.toFixed(0)}%</td>
                <td className="px-4 py-2.5 text-right text-slate-400 font-mono">{r.pct_symbols_stable?.toFixed(0)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function ReverseBacktestPanel() {
  const [status, setStatus] = useState(null)
  const [results, setResults] = useState(null)
  const [lastRun, setLastRun] = useState(null)
  const [starting, setStarting] = useState(false)
  const [startError, setStartError] = useState(null)
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [symbols, setSymbols] = useState('')
  const [days, setDays] = useState(90)
  const [minBars, setMinBars] = useState(2000)
  const [topN, setTopN] = useState(5)
  const [activeOnly, setActiveOnly] = useState(false)

  const refreshResults = useCallback(async () => {
    try {
      const r = await reverseBacktest.results()
      if (r.success && r.data) { setResults(r.data); setLastRun(r.last_run) }
    } catch { /* non-fatal */ }
  }, [])

  const refreshStatus = useCallback(async () => {
    try {
      const d = await reverseBacktest.status()
      if (d.success) setStatus(d.status)
      return d.status
    } catch { return null }
  }, [])

  // Initial load: pick up an in-progress or previously completed run.
  useEffect(() => {
    refreshStatus()
    refreshResults()
  }, [refreshStatus, refreshResults])

  // Poll while running.
  useEffect(() => {
    if (status?.state !== 'running') return
    const id = setInterval(async () => {
      const s = await refreshStatus()
      if (s?.state === 'completed') await refreshResults()
    }, POLL_MS)
    return () => clearInterval(id)
  }, [status?.state, refreshStatus, refreshResults])

  const handleRun = async () => {
    setStarting(true)
    setStartError(null)
    try {
      const body = { days, min_bars: minBars, top_n: topN, active_only: activeOnly }
      if (symbols.trim()) body.symbols = symbols.trim()
      const res = await reverseBacktest.run(body)
      if (res.httpStatus === 202) {
        setStatus({ state: 'running', last_message: 'starting', started_at: new Date().toISOString() })
      } else if (res.httpStatus === 409) {
        setStatus(res.status || { state: 'running' })
      } else {
        setStartError(res.error || `Failed to start (HTTP ${res.httpStatus})`)
      }
    } catch (e) {
      setStartError(e.message)
    }
    setStarting(false)
  }

  const running = status?.state === 'running'

  return (
    <div className="space-y-4">
      <p className="text-slate-400 text-sm leading-relaxed" style={{ maxWidth: '80ch' }}>{REVERSE_BACKTEST_DESCRIPTION}</p>

      <div className="rounded-lg p-4 bg-amber-900/10 border border-amber-700/30 flex items-start gap-2.5">
        <AlertTriangle className="h-4 w-4 text-amber-400 flex-shrink-0 mt-0.5" />
        <p className="text-amber-200/80 text-xs leading-relaxed min-w-0 break-words" style={{ overflowWrap: 'anywhere' }}>
          In-sample only -- fixed common settings, no fitting, so nothing here is
          overfit, but it is still just a backtest and is not calibrated against real
          fills. This project's own measured backtest-vs-real gap runs roughly
          -0.2 to -0.3 percentage points/trade (see
          docs/SAFERFUNDS_STRATEGY_RESEARCH_LOG_2026_09.md, Experiment 3). Read these
          numbers as relative evidence, not a forecast.
        </p>
      </div>

      {/* Run controls */}
      <div className="rounded-lg border border-slate-800 p-4 space-y-3" style={{ background: '#0d1321' }}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={handleRun}
              disabled={starting || running}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-xs tracking-wide transition-colors ${
                starting || running
                  ? 'bg-slate-800 text-slate-600 cursor-not-allowed'
                  : 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 hover:bg-cyan-500/30'
              }`}
              style={{ fontFamily: 'Rajdhani, sans-serif', letterSpacing: '0.08em' }}
            >
              {running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
              {running ? 'RUNNING...' : results ? 'RUN AGAIN' : 'RUN SEARCH'}
            </button>
            {status?.state && <StateBadge state={status.state} />}
          </div>
          <button
            onClick={() => setShowAdvanced(v => !v)}
            className="text-slate-500 hover:text-slate-300 text-xs flex items-center gap-1"
          >
            <ChevronRight className={`h-3 w-3 transition-transform ${showAdvanced ? 'rotate-90' : ''}`} />
            Advanced options
          </button>
        </div>

        {showAdvanced && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2 border-t border-slate-800/60">
            <label className="text-xs text-slate-500 flex flex-col gap-1">
              Symbols (optional)
              <input
                value={symbols}
                onChange={e => setSymbols(e.target.value)}
                placeholder="every ticker"
                disabled={running}
                className="bg-slate-900 border border-slate-800 rounded px-2 py-1 text-slate-300 font-mono text-xs"
              />
            </label>
            <label className="text-xs text-slate-500 flex flex-col gap-1">
              Days (1-90)
              <input
                type="number" min={1} max={90} value={days} disabled={running}
                onChange={e => setDays(Number(e.target.value))}
                className="bg-slate-900 border border-slate-800 rounded px-2 py-1 text-slate-300 font-mono text-xs"
              />
            </label>
            <label className="text-xs text-slate-500 flex flex-col gap-1">
              Min bars/symbol
              <input
                type="number" min={100} max={100000} value={minBars} disabled={running}
                onChange={e => setMinBars(Number(e.target.value))}
                className="bg-slate-900 border border-slate-800 rounded px-2 py-1 text-slate-300 font-mono text-xs"
              />
            </label>
            <label className="text-xs text-slate-500 flex flex-col gap-1">
              Top-N for combos
              <input
                type="number" min={2} max={8} value={topN} disabled={running}
                onChange={e => setTopN(Number(e.target.value))}
                className="bg-slate-900 border border-slate-800 rounded px-2 py-1 text-slate-300 font-mono text-xs"
              />
            </label>
            <label className="text-xs text-slate-500 flex items-center gap-2 col-span-2 sm:col-span-4">
              <input
                type="checkbox" checked={activeOnly} disabled={running}
                onChange={e => setActiveOnly(e.target.checked)}
              />
              Active watchlist only (default: every ticker in the database)
            </label>
          </div>
        )}

        {startError && (
          <p className="text-rose-400 text-xs font-mono">{startError}</p>
        )}
        {running && status?.last_message && (
          <p className="text-slate-500 text-xs font-mono">
            {status.last_message}
            {status.started_at && ` · started ${timeAgo(status.started_at)}`}
          </p>
        )}
        {status?.state === 'failed' && status?.error && (
          <pre className="text-rose-400 text-[11px] font-mono whitespace-pre-wrap max-h-40 overflow-y-auto bg-rose-950/20 rounded p-2 border border-rose-900/40">
            {status.error}
          </pre>
        )}
      </div>

      {/* Results */}
      {results && results.status === 'ok' && (
        <>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="text-slate-500 text-xs font-mono">
              Universe: {results.universe_size} symbols · {results.days}-day lookback ·
              {' '}timebox {results.timebox_min}min
              {results.top_indicators?.length > 0 && (
                <> · top performers feeding combos: {results.top_indicators.join(', ')}</>
              )}
            </p>
            {lastRun && (
              <span className="text-slate-600 text-xs flex items-center gap-1">
                <RefreshCw className="h-3 w-3" /> last run {timeAgo(lastRun)}
              </span>
            )}
          </div>
          <ResultsTable title="SINGLE INDICATORS (COMMON SETTINGS)" rows={results.single_summary} />
          <ResultsTable title="COMBINATIONS (TOP PERFORMERS, PAIRWISE + 3-WAY)" rows={results.combo_summary} />
        </>
      )}

      {results && results.status === 'empty' && (
        <p className="text-slate-500 text-xs font-mono">{results.message}</p>
      )}
    </div>
  )
}

function PastTestPanel({ test }) {
  return (
    <div className="space-y-4">
      <p className="text-slate-400 text-sm leading-relaxed" style={{ maxWidth: '80ch' }}>{test.description}</p>
      <div className="rounded-lg border border-slate-800 p-4 space-y-2" style={{ background: '#0d1321' }}>
        <div className="flex items-center gap-2 text-slate-500 text-xs">
          <Terminal className="h-3.5 w-3.5" />
          <span>Not wired for on-demand runs from this dashboard -- run from an environment with database access:</span>
        </div>
        <code className="block text-emerald-400 text-xs font-mono bg-slate-900 rounded px-3 py-2 overflow-x-auto">
          {test.usage}
        </code>
        <p className="text-slate-600 text-xs">Reference: {test.docs}</p>
      </div>
    </div>
  )
}

// ── Counterfactual exit replay (T-187) ────────────────────────────────────────────
// Replays the MAIN book's closed trades on 1-minute bars under wider stop floors, an ATR band and a timebox, with a 1-bar
// decision-to-fill delay, and scores them out of sample. Research only: nothing here changes a live parameter.
const POLICY_LABELS = {
  'main_now_floor2.5_trail6': 'Baseline: 2.5% floor, 6% trail',
  'floor4.0_trail6': '4.0% floor, 6% trail',
  'floor6.0_trail6': '6.0% floor, 6% trail',
  'floor2.5_atr2.5x': '2.5% floor, 2.5x ATR band',
  'floor2.5_trail6_cap120': '2.5% floor, 6% trail, 120-min timebox',
}
const VERDICT_MIN_OOS = 20

const fmtSigned = (v) => (v === null || v === undefined ? 'n/a' : `${v > 0 ? '+' : ''}${Number(v).toFixed(3)}%`)

function ReplayToast({ result, onClose }) {
  const pooled = result.oos_pooled || []
  const base = pooled.find(r => r.name === 'main_now_floor2.5_trail6')
  const best = pooled.filter(r => r.mean !== null && r.mean !== undefined).sort((a, b) => b.mean - a.mean)[0]
  const gated = (result.n_oos || 0) >= VERDICT_MIN_OOS
  return (
    <div role="status" className="rounded-lg border border-cyan-500/40 p-4 space-y-3" style={{ background: '#0b1a24' }}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-cyan-300 text-xs tracking-widest font-semibold">COUNTERFACTUAL EXIT REPLAY — MAIN BOOK, LAST {result.days} DAYS</p>
          <p className="text-slate-500 text-xs mt-1">
            {result.n_replayed} trades replayed, {result.n_skipped} skipped for bars ({(result.skipped_share_pct ?? 0).toFixed(1)}% non-replayable);
            {' '}{result.analyzable} analyzable of {result.closed_rows} closed (excluded {(result.excluded_share_pct ?? 0).toFixed(1)}%)
            {result.capped ? `; capped to the ${result.n_replayed + result.n_skipped} most recent` : ''}.
          </p>
        </div>
        <button onClick={onClose} className="text-slate-500 hover:text-slate-300 text-xs flex-shrink-0">Dismiss</button>
      </div>

      {pooled.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-slate-600 border-b border-slate-800/60">
                <th className="px-3 py-2 text-left">Exit policy (out-of-sample test days only)</th>
                <th className="px-3 py-2 text-right">n</th>
                <th className="px-3 py-2 text-right">Mean P&amp;L/trade</th>
                <th className="px-3 py-2 text-right">vs baseline</th>
                <th className="px-3 py-2 text-right">Win rate</th>
              </tr>
            </thead>
            <tbody>
              {pooled.map(r => (
                <tr key={r.name} className="border-b border-slate-800/30">
                  <td className="px-3 py-2 text-slate-300">{POLICY_LABELS[r.name] || r.name}</td>
                  <td className="px-3 py-2 text-right text-slate-400 font-mono">{r.n}</td>
                  <td className={`px-3 py-2 text-right font-mono font-semibold ${(r.mean ?? 0) >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>{fmtSigned(r.mean)}</td>
                  <td className="px-3 py-2 text-right text-slate-400 font-mono">{r.name === 'main_now_floor2.5_trail6' ? '--' : fmtSigned(r.delta_vs_baseline)}</td>
                  <td className="px-3 py-2 text-right text-slate-400 font-mono">{r.win_pct === null || r.win_pct === undefined ? 'n/a' : `${r.win_pct.toFixed(1)}%`}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-amber-300/80 text-xs">No out-of-sample result: the window needs trades on at least two trading days with complete bars.</p>
      )}

      {/* Verdict gate: a structural verdict is shown only with >= 20 pooled out-of-sample trades. */}
      {gated && base && best ? (
        <p className="text-slate-300 text-xs">
          Research lead ({result.n_oos} pooled OOS trades): best on test days is <span className="text-cyan-300">{POLICY_LABELS[best.name] || best.name}</span> at {fmtSigned(best.mean)}/trade
          {best.name === 'main_now_floor2.5_trail6' ? ', i.e. no counterfactual beat the current floor.' : `, ${fmtSigned(best.delta_vs_baseline)} against the current floor. A family-level claim needs 100 pooled OOS trades.`}
        </p>
      ) : (
        <p className="text-slate-500 text-xs">No verdict: {result.n_oos || 0} pooled out-of-sample trades (a research lead needs {VERDICT_MIN_OOS}).</p>
      )}
      <p className="text-slate-600 text-[11px]">{result.caption || 'paper entries, relative evidence, n and excluded share'}; 1-bar decision-to-fill delay, {result.half_spread_pct}% half spread on the sell. Backtests here have overstated real edge about 10x; read as A vs B, not a forecast.</p>
    </div>
  )
}

function ExitReplayPanel() {
  const [status, setStatus] = useState(null)
  const [days, setDays] = useState(30)
  const [starting, setStarting] = useState(false)
  const [startError, setStartError] = useState(null)
  const [toast, setToast] = useState(null)

  const refreshStatus = useCallback(async () => {
    try {
      const d = await exitReplay.status()
      if (d.success) setStatus(d.status)
      return d.status
    } catch { return null }
  }, [])

  useEffect(() => { refreshStatus() }, [refreshStatus])

  // Poll while running; on completion, pop the result toast without a page refresh.
  useEffect(() => {
    if (status?.state !== 'running') return
    const id = setInterval(async () => {
      const s = await refreshStatus()
      if (s?.state === 'completed' && s.result) setToast(s.result)
    }, POLL_MS)
    return () => clearInterval(id)
  }, [status?.state, refreshStatus])

  const handleRun = async () => {
    setStarting(true)
    setStartError(null)
    setToast(null)
    try {
      const res = await exitReplay.run(days)
      if (res.httpStatus === 202) setStatus(res.status)
      else if (res.httpStatus === 409) setStatus(res.status || { state: 'running' })
      else setStartError(res.error || `Failed to start (HTTP ${res.httpStatus})`)
    } catch (e) {
      setStartError(e.message)
    }
    setStarting(false)
  }

  const running = status?.state === 'running'
  const lastResult = status?.state === 'completed' ? status.result : null

  return (
    <div className="space-y-4">
      <p className="text-slate-400 text-sm leading-relaxed" style={{ maxWidth: '80ch' }}>
        Re-runs how the MAIN book would have exited its recent closed trades if the stop floor were wider (4% or 6% instead of 2.5%),
        trailed a 2.5x ATR band, or had a 120-minute timebox, on the stored 1-minute bars. Each exit is decided on a bar close and filled
        on the next bar, less a half spread. Policies are scored on later days than the ones used to choose them.
      </p>
      <div className="rounded-lg p-4 bg-amber-900/10 border border-amber-700/30 flex items-start gap-2.5">
        <AlertTriangle className="h-4 w-4 text-amber-400 flex-shrink-0 mt-0.5" />
        <p className="text-amber-200/80 text-xs leading-relaxed min-w-0 break-words" style={{ overflowWrap: 'anywhere' }}>
          Research only: this reads closed trades and bars and changes no live setting. Entries are the recorded paper fills; only the
          exits are simulated.
        </p>
      </div>

      <div className="rounded-lg border border-slate-800 p-4 space-y-3" style={{ background: '#0d1321' }}>
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={handleRun}
            disabled={starting || running}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-xs tracking-wide transition-colors ${
              starting || running
                ? 'bg-slate-800 text-slate-600 cursor-not-allowed'
                : 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 hover:bg-cyan-500/30'
            }`}
            style={{ fontFamily: 'Rajdhani, sans-serif', letterSpacing: '0.08em' }}
          >
            {running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
            {running ? 'RUNNING...' : 'Run Counterfactual Exit Replay'}
          </button>
          <label className="text-xs text-slate-500 flex items-center gap-2">
            Lookback days
            <input
              type="number" min={1} max={90} value={days} disabled={running}
              onChange={e => setDays(Number(e.target.value))}
              className="w-16 bg-slate-900 border border-slate-800 rounded px-2 py-1 text-slate-300 font-mono text-xs"
            />
          </label>
          {status?.state && <StateBadge state={status.state} />}
        </div>
        {running && <p className="text-slate-500 text-xs">{status.last_message || 'running'}</p>}
        {status?.state === 'failed' && <p className="text-rose-400 text-xs break-words">{status.error || 'failed'}</p>}
        {startError && <p className="text-rose-400 text-xs break-words">{startError}</p>}
      </div>

      {toast && <ReplayToast result={toast} onClose={() => setToast(null)} />}
      {!toast && lastResult && (
        <button onClick={() => setToast(lastResult)} className="text-cyan-400 hover:text-cyan-300 text-xs">
          Show the last completed result ({status.finished_at ? timeAgo(status.finished_at) : 'earlier'})
        </button>
      )}
    </div>
  )
}

export default function Testing() {
  const [selectedId, setSelectedId] = useState(REVERSE_BACKTEST_ID)
  const [modalCard, setModalCard] = useState(null)   // T-194: the card whose formula and statistics modal is open

  const cards = [
    {
      id: REVERSE_BACKTEST_ID,
      name: 'Reverse Indicator Search',
      tagline: '90-day · 1-minute · runs live',
      runnable: true,
    },
    {
      id: OPTIMIZER_ID,
      name: 'Walk-Forward Optimizer',
      tagline: 'last persisted out-of-sample run · read-only',
      runnable: true,
    },
    {
      id: EXIT_REPLAY_ID,
      name: 'Counterfactual Exit Replay',
      tagline: 'MAIN stop floors, ATR band, timebox · out of sample · runs live',
      runnable: true,
    },
    {
      id: RECONSTRUCTION_ID,
      name: 'Parameter Reconstruction',
      tagline: 'point-in-time ticker fits for historical replays · run status · read-only',
      runnable: true,
    },
    ...PAST_TESTS.map(t => ({ id: t.id, name: t.name, tagline: t.tagline, runnable: false })),
  ]

  const selectedPastTest = PAST_TESTS.find(t => t.id === selectedId)

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-slate-200 font-semibold text-sm tracking-widest flex items-center gap-2"
            style={{ fontFamily: 'Rajdhani, sans-serif' }}>
          <FlaskConical className="h-4 w-4 text-cyan-400" />
          TESTING
        </h2>
        <p className="text-slate-500 text-xs mt-0.5">
          Backtest types available for this system -- select one to see what it does and its results.
        </p>
      </div>

      {/* Select buttons */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {cards.map(c => {
          const active = selectedId === c.id
          return (
            <button
              key={c.id}
              onClick={() => { setSelectedId(c.id); setModalCard(c) }}
              className="text-left rounded-xl p-4 transition-all"
              style={{
                background: active ? 'rgba(34,211,238,0.06)' : '#111827',
                border: active ? '1px solid rgba(34,211,238,0.4)' : '1px solid #1f2937',
                boxShadow: active ? '0 0 0 1px rgba(34,211,238,0.15)' : 'none',
              }}
            >
              <div className="flex items-center justify-between">
                <p className={`font-semibold text-sm ${active ? 'text-cyan-300' : 'text-slate-300'}`}
                   style={{ fontFamily: 'Rajdhani, sans-serif', letterSpacing: '0.04em' }}>
                  {c.name}
                </p>
                {c.runnable
                  ? <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-emerald-900/40 text-emerald-300 border border-emerald-700/40">LIVE</span>
                  : <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-800 text-slate-500 border border-slate-700">CLI</span>
                }
              </div>
              <p className="text-slate-500 text-xs mt-1">{c.tagline}</p>
            </button>
          )
        })}
      </div>

      {/* Detail panel */}
      {selectedId === REVERSE_BACKTEST_ID && <ReverseBacktestPanel />}
      {selectedId === OPTIMIZER_ID && <OptimizerSummaryPanel />}
      {selectedId === EXIT_REPLAY_ID && <ExitReplayPanel />}
      {selectedId === RECONSTRUCTION_ID && <ReconstructionPanel />}
      {selectedPastTest && <PastTestPanel test={selectedPastTest} />}
      {modalCard && <StrategyCardModal card={modalCard} onClose={() => setModalCard(null)} onOpenPanel={setSelectedId} />}
    </div>
  )
}
